<template>
  <header>
    <div
      class="relative flex flex-col"
      :class="amountSponsorsOrOrganizers ? 'min-h-[50vh]' : 'min-h-[65vh]'"
    >
      <!-- IMAGE OR VIDEO -->
      <div class="absolute inset-0 -z-10 overflow-hidden">
        <AppImage
          v-if="imageOrVideo.image"
          :src="imageOrVideo.image"
          :alt="
            $i18n.locale === 'fr'
              ? imageOrVideo.image.alt_fr
              : imageOrVideo.image.alt
          "
          class="h-full w-full object-cover"
          :style="`object-position: ${imageOrVideo.image.focus_css};`"
        />

        <AppImage
          v-else-if="
            imageOrVideo.video &&
            data.hero_only_image &&
            imageOrVideo.cover_image
          "
          :src="imageOrVideo.cover_image"
          class="h-full w-full object-cover"
          :style="`object-position: ${imageOrVideo.cover_image.focus_css};`"
        />

        <VideoPlayer
          v-else-if="imageOrVideo.video"
          :src="imageOrVideo.video"
          :autoplay="true"
          class="h-full w-full scale-[1.2] transition-transform"
        />
        <div
          v-else
          class="h-full w-full bg-gradient-to-tr from-blue-dark-800 to-blue-dark-600"
        />
      </div>

      <HeroImageGradient />

      <div
        class="z-10 flex w-full grow flex-col justify-end pb-12 pt-navigation-desktop text-white"
      >
        <Container indent="center">
          <div>
            <!-- BACK LINK -->
            <button
              class="group -m-1 my-5 flex items-center gap-3 p-1 text-base ring-offset-0 sm:my-7"
              @click="$router.back()"
            >
              <ArrowGrowIcon class="-ml-2 w-6 rotate-180" />
              <span class="first-letter:uppercase">
                {{ $t('back') }}
              </span>
            </button>

            <!-- TITLE -->
            <div v-if="data.event_date_from" class="mb-5 text-white/60">
              <time :datetime="data.event_date_from" class="inline-block">
                {{ formateDate(data.event_date_from) }}
              </time>

              <span>&nbsp;-&nbsp;</span>

              <time :datetime="data.date" class="inline-block">
                {{ formateDate(data.date) }}
              </time>
            </div>
            <AnimatedContainer>
              <h1
                v-if="data.heading"
                class="fade-in mb-4 max-w-title hyphens-auto text-3xl font-medium leading-9 sm:mb-6 sm:text-6xl"
                v-html="$replaceHtmlTags(data.heading)"
              />

              <p
                v-if="data.description || data.event_location"
                class="fade-in mb-7 inline-block text-white/60 sm:mb-12"
              >
                {{ data.description }}
                <span v-if="data.description && data.event_location">
                  &nbsp;|&nbsp;
                </span>
                {{ data.event_location }}
              </p>
            </AnimatedContainer>
          </div>
        </Container>
      </div>
    </div>

    <!-- SPONSOR AND ORGANIZER LOGOS -->
    <div
      v-if="amountSponsorsOrOrganizers"
      class="min-h-[8rem] w-full bg-blue-10 pb-7 pt-4"
    >
      <AnimatedContainer>
        <div
          :class="
            amountSponsorsOrOrganizers > 3
              ? 'mx-auto w-full max-w-[68rem] px-container sm:px-container-desktop xl:sm:px-container-desktop-wide xl:mx-0 xl:max-w-none'
              : 'mx-auto w-full max-w-[68rem] px-container sm:px-container-desktop'
          "
        >
          <div
            class="flex flex-col justify-between gap-x-8 gap-y-4 md:gap-x-16"
            :class="
              amountSponsorsOrOrganizers > 3 ? 'xl:flex-row' : 'md:flex-row'
            "
          >
            <!-- MAIN SPONSORS -->
            <div v-if="data?.main_sponsors" class="flex flex-col">
              <p class="mb-3 text-sm opacity-60">
                {{ data.main_sponsors_title ?? $t('main_sponsors') }}
              </p>
              <div class="flex grow gap-4 md:gap-10">
                <div
                  v-for="sponsor in data.main_sponsors"
                  :key="sponsor.id"
                  class="flex max-w-[15rem] items-center"
                >
                  <NuxtLink
                    :to="sponsor.link"
                    :target="$useTarget(sponsor.link)"
                    class="slide-in-left max-h-[4rem]"
                  >
                    <AppImage
                      v-if="sponsor.logo"
                      :alt="
                        $i18n.locale === 'fr'
                          ? sponsor.logo.alt_fr
                          : sponsor.logo.alt
                      "
                      :src="sponsor.logo"
                    />
                  </NuxtLink>
                </div>
              </div>
            </div>
            <!-- ORGANIZERS -->
            <div v-if="data?.organizers" class="flex flex-col">
              <p class="mb-3 text-sm opacity-60">
                {{ data.organizers_title ?? $t('organizers') }}
              </p>
              <div class="flex w-full grow gap-4 md:gap-10">
                <div
                  v-for="organizer in data.organizers"
                  :key="organizer.id"
                  class="flex max-w-[15rem] items-center"
                >
                  <NuxtLink
                    :to="organizer.link"
                    :target="$useTarget(organizer.link)"
                    class="slide-in-left max-h-[4rem]"
                  >
                    <AppImage
                      v-if="organizer.logo"
                      :alt="
                        $i18n.locale === 'fr'
                          ? organizer.logo.alt_fr
                          : organizer.logo.alt
                      "
                      :src="organizer.logo"
                    />
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedContainer>
    </div>

    <!-- LEAD -->
    <Container v-if="data.lead" indent="center">
      <div class="max-w-text pt-16 text-md font-medium" v-html="data.lead" />
    </Container>
  </header>
</template>

<script setup>
const { t } = useI18n()

const props = defineProps({
  data: { type: Object, required: true },
})

// COMPUTED
const imageOrVideo = computed(() => {
  return props.data?.image_or_video?.[0] || false
})

const amountSponsorsOrOrganizers = computed(() => {
  return (
    (props.data?.main_sponsors?.length || 0) +
    (props.data?.organizers?.length || 0)
  )
})

// FUNCTIONS
function formateDate(date) {
  if (!date) {
    return ''
  }

  date = new Date(date)

  const dateOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }

  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
  }

  const formattedDate = date.toLocaleDateString('de', dateOptions)
  const formattedTime = date.toLocaleTimeString('de', timeOptions)
  const customSeparator = ' '

  return formattedDate + customSeparator + formattedTime + ` ${t('o_clock')}`
}
</script>
